/**
 * Emissão na fábrica: Prévia → Conferir → Confirmar emissão. A prévia é o MESMO cálculo da
 * emissão (servidor); se algo mudar entre a conferência e a confirmação, a API recusa e
 * mostra a nova prévia para conferir de novo.
 */
import {
  FISCAL_USE_LABEL,
  formatMoney,
  type FiscalDocumentDetail,
  type FiscalLink,
  type FiscalPreview,
  type FiscalUse,
} from "@cafegestor/shared";
import { useEffect, useState } from "react";
import { useCan } from "@/app/me";
import { Alert, Button, Card } from "@/components/ui";
import { ApiRequestError, apiFetch } from "@/lib/api";
import { Checkbox, SaveError } from "../cadastros/widgets";
import { CalcView } from "./CalcView";
import { DocStatusBadge, DocumentDetail } from "./DocumentDetail";
import { TPAG_LABEL, useFiscalAction, useFiscalQuery } from "./lib";

const ORIGIN_SEGMENT: Record<FiscalUse, string> = {
  venda_fabrica: "venda",
  venda_rota: "venda",
  remessa_carga: "carregamento",
  retorno_carga: "acerto",
  divergencia_falta: "acerto",
  divergencia_sobra: "acerto",
  devolucao_venda: "devolucao",
};

export function EmissionPanel({
  use,
  sourceId,
  title,
}: {
  use: FiscalUse;
  sourceId: string;
  title?: string;
}) {
  const can = useCan();
  const links = useFiscalQuery<FiscalLink[]>(
    `/origem/${ORIGIN_SEGMENT[use]}/${sourceId}`,
    can("fiscal.ver"),
  );
  const [preview, setPreview] = useState<FiscalPreview | null>(null);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const emit = useFiscalAction<
    { use: FiscalUse; sourceId: string; previewHash: string; model: "55" | "65" | null },
    FiscalDocumentDetail
  >(
    () => "/emitir",
    "POST",
    (doc) => {
      setPreview(null);
      setChecked(false);
      setOpened(doc.id);
    },
  );
  const mine = (links.data ?? []).filter((l) => l.use === use);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    setChecked(false);
    emit.reset(); // a prévia nova substitui a que veio do erro anterior
    try {
      setPreview(
        await apiFetch<FiscalPreview>("/fiscal/previa", {
          method: "POST",
          body: { use, sourceId, model: null },
        }),
      );
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // prévia desatualizada: a API devolve a nova prévia para conferir de novo
  const emitError = emit.error;
  const staleHash =
    emitError instanceof ApiRequestError && emitError.code === "PREVIA_DESATUALIZADA"
      ? emitError
      : null;
  // a conferência valia para a prévia anterior: marcar de novo
  useEffect(() => {
    if (staleHash) setChecked(false);
  }, [staleHash]);
  const refreshed =
    emitError instanceof ApiRequestError && emitError.code === "PREVIA_DESATUALIZADA"
      ? ((emitError.details as { preview?: FiscalPreview } | undefined)?.preview ?? null)
      : null;

  // sem acesso ao fiscal, a tela de origem não mostra o painel (a API recusaria de qualquer modo)
  if (!can("fiscal.ver")) return null;

  return (
    <Card title={title ?? `Nota fiscal — ${FISCAL_USE_LABEL[use]}`}>
      <div className="space-y-3">
        {mine.map((l) => (
          <div key={l.documentId} className="flex flex-wrap items-center gap-2 text-sm">
            <span>
              {l.model === "65" ? "NFC-e" : "NF-e"} {l.series}-{l.number}
            </span>
            <DocStatusBadge status={l.status} />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setOpened(opened === l.documentId ? null : l.documentId)}
            >
              {opened === l.documentId ? "Fechar" : "Abrir"}
            </Button>
          </div>
        ))}
        {opened && <DocumentDetail id={opened} onClose={() => setOpened(null)} />}
        <Button variant="secondary" onClick={loadPreview} disabled={loading}>
          {loading ? "Calculando..." : "Prévia fiscal"}
        </Button>
        <SaveError error={error} />
        {refreshed && (
          <Alert tone="warn">
            O cálculo mudou desde a sua conferência (regra, preço ou cadastro). Confira a nova
            prévia abaixo.
          </Alert>
        )}
        {(refreshed ?? preview) && <PreviewBlock p={(refreshed ?? preview)!} />}
        {(refreshed ?? preview)?.canEmit && can("fiscal.documento.emitir") && (
          <div className="space-y-2 rounded-md border p-3">
            <Checkbox
              label="Conferi destinatário, itens, CFOP, tributos, totais e pagamentos."
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <Button
              disabled={!checked || emit.isPending}
              onClick={() => {
                const p = (refreshed ?? preview)!;
                emit.mutate({ use, sourceId, previewHash: p.hash, model: p.calc.model });
              }}
            >
              {emit.isPending ? "Enviando..." : "Confirmar emissão"}
            </Button>
          </div>
        )}
        {!refreshed && <SaveError error={emitError} />}
      </div>
    </Card>
  );
}

function PreviewBlock({ p }: { p: FiscalPreview }) {
  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="text-sm">
        <strong>{p.sourceLabel}</strong> — {p.recipient.name} {p.recipient.document ?? ""} (
        {p.recipient.uf ?? "UF?"}) · ambiente {p.environment} · provedor {p.provider}
      </div>
      {p.existingDocumentId && (
        <Alert tone="warn">Esta origem já tem documento fiscal ativo.</Alert>
      )}
      <CalcView calc={p.calc} />
      {p.payments.length > 0 && (
        <p className="text-sm">
          Pagamentos:{" "}
          {p.payments
            .map(
              (x) =>
                `${TPAG_LABEL[x.tpag] ?? x.tpag} ${formatMoney(x.amount)}${x.electronic ? " (eletrônico)" : ""}`,
            )
            .join(" · ")}
        </p>
      )}
      {p.references.length > 0 && (
        <p className="text-sm">
          Referencia: {p.references.map((r) => `${r.kind} nº ${r.number ?? "?"}`).join(", ")}
        </p>
      )}
    </div>
  );
}
